/**
 * Dónde está el API. UN SOLO SITIO.
 *
 * Aquí se lee `VITE_API_URL` y aquí vive el prefijo; el cliente JSON, el
 * transporte del chat, las subidas y el WebSocket del dictado lo piden de
 * aquí. Antes cada uno leía la variable por su cuenta, y cambiar de host --o
 * de prefijo, como al pasar por el gateway-- era perseguirla por cinco
 * archivos.
 */

/**
 * El host del API, sin barra final. Vacío es "el mismo origen": en desarrollo,
 * el proxy de Vite. Se hornea al compilar (§12), no se lee en ejecución.
 */
export const API_URL: string = (import.meta.env.VITE_API_URL ?? "").replace(
  /\/+$/,
  "",
);

export const API_VERSION = "v1";

/**
 * El prefijo de las rutas. Lleva el nombre del servicio porque el gateway de
 * lala-ms no reescribe el path y cuelga cada servicio de `/api/<servicio>`
 * (§10.2). El backend declara el suyo en `transport/rest/controller.ts`: los
 * dos tienen que cambiar a la vez.
 */
export const API_BASE = `/api/ccmas/${API_VERSION}` as const;

/** La URL completa de una ruta del API: `apiUrl(`${API_BASE}/me`)`. */
export const apiUrl = (path: string): string => `${API_URL}${path}`;

/**
 * El prefijo de las rutas que ccmas ya tenía antes del núcleo de revisión
 * (distribuidores, participantes, periodos, SKU, objetivos, tareas, usuarios).
 * Hoy es el MISMO servicio (`backend/apps/ccmas` en lala-ms) y la MISMA base
 * que `API_BASE`: esos controllers viven bajo `RouterModule` con la ruta `v1`
 * (`/api/ccmas/v1/distributors`, …), y los recursos del núcleo nuevo
 * (`conversations`, `alfred-tickets`, `commands`, …) no chocan con ellos.
 * Se conserva el nombre aparte porque esas rutas responden con el formato y
 * los errores de Nest, que el cliente legacy (`legacySend`/`legacyRequest`)
 * sabe leer. Cuando esas acciones pasen al catálogo (`defineTool`) con sus
 * mismas rutas, el cliente legacy se puede retirar.
 */
export const LEGACY_API_BASE = API_BASE;

/** La URL completa de una ruta del microservicio viejo: `legacyApiUrl("/distributors")`. */
export const legacyApiUrl = (path: string): string =>
  `${API_URL}${LEGACY_API_BASE}${path}`;

/**
 * La misma ruta, por WebSocket. Sin host configurado se usa el de la página,
 * que es a donde apunta el proxy de Vite.
 */
export const wsUrl = (path: string): string => {
  const http = API_URL === "" ? globalThis.location.origin : API_URL;
  return `${http.replace(/^http/, "ws")}${path}`;
};

/**
 * Una ruta del microservicio viejo por SSE (`lib/sse.ts`), con el token en la
 * URL: un `EventSource`, como un WebSocket, no admite cabeceras, así que no
 * puede llevar el `Authorization` de siempre (`currentToken` en `auth.ts`).
 */
export const legacySseUrl = (
  path: string,
  token: string | undefined,
): string => {
  const query =
    token === undefined ? "" : `?token=${encodeURIComponent(token)}`;
  return `${legacyApiUrl(path)}${query}`;
};
